import entries from "lodash/entries";
import fromPairs from "lodash/fromPairs";
import get from "lodash/get";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import isObject from "lodash/isObject";
import mapValues from "lodash/mapValues";
import omit from "lodash/omit";
import parseInt from "lodash/parseInt";
import { camelizeApiKey, camelizeApiKeys } from "PFCore/api/utils";
import { buildOriginalFilterPath, getEmptyValue } from "PFCore/helpers/filters";
import { omitUndefined } from "PFCore/helpers/object";
import { Filter, Filters, Snakify, Value } from "PFTypes";
import {
  ApiFilterGroupV2,
  FilteredCollectionKey,
  FilterKeyV2,
  FiltersMetaV2,
  FiltersV2,
  FilterTypeV2,
  FilterV2,
  FilterValueV2,
  KnownFiltersV2,
  StandardFilterV2
} from "PFTypes/filters_v2";

function isNumeric(value: string) {
  return /^-?\d+$/.test(value);
}

const parseNumericString = (numericValue: any) => {
  if (typeof numericValue === "string" && isNumeric(numericValue)) {
    return parseInt(numericValue);
  }

  return numericValue;
};

const FILTER_TYPES_WITH_IDS = [
  FilterTypeV2.SelectMany,
  FilterTypeV2.SelectOne,
  FilterTypeV2.Checkboxes,
  FilterTypeV2.Radio
];

const parseApiFilterValue = (value: Value | undefined, { type }: Pick<FilterV2, "type">) => {
  if (FILTER_TYPES_WITH_IDS.includes(type)) {
    return (
      isArray(value) ? value.map((id) => parseNumericString(id)) : parseNumericString(value)
    ) as FilterValueV2;
  }

  return value as FilterValueV2;
};

const hasMinimalRequiredFilterDefinition = (filter: any | Partial<Filter>): filter is Filter =>
  isObject(filter) && !isEmpty(filter) && "type" in filter;

const parseApiFilter = (
  group: ApiFilterGroupV2,
  { ordinal, value, type, mandatory, ...legacyFilter }: Filter,
  defaultFilterValues?: Filters<Value>
): FilterV2 => {
  const name = camelizeApiKey(legacyFilter.name as string) as FilterKeyV2;
  const originalPath = buildOriginalFilterPath(group, name);
  const defaultValue = parseApiFilterValue(get(defaultFilterValues || {}, originalPath), { type });

  return omitUndefined({
    ...legacyFilter,
    type,
    name: name,
    ordinal: parseNumericString(ordinal),
    value: parseApiFilterValue(value, { type }) || getEmptyValue({ type }),
    defaultValue,
    originalPath,
    group,
    required: mandatory
  } as FilterV2);
};

const parseApiFilters = (
  snakifiedLegacyFilters: Snakify<Filters>,
  snakifiedDefaultFilters?: Snakify<Filters<Value>>
): FiltersV2 => {
  const legacyFilters = camelizeApiKeys(snakifiedLegacyFilters);
  const defaultFilters = camelizeApiKeys(snakifiedDefaultFilters);
  const filterGroups = entries(omit(legacyFilters, [ApiFilterGroupV2.Availability, "children"]));

  const filterPairs = filterGroups.flatMap(([group, filters]) =>
    entries(filters)
      .filter(([, filter]) => hasMinimalRequiredFilterDefinition(filter))
      .map(([name, filter]) => {
        const parsedFilter = parseApiFilter(
          group as ApiFilterGroupV2,
          { name, ...(filter as Filter) },
          defaultFilters as Filters<Value>
        );

        return [parsedFilter.name, parsedFilter];
      })
  ) as [FilterKeyV2, StandardFilterV2][];

  const filters = fromPairs<StandardFilterV2>(filterPairs) as FiltersV2;
  if (legacyFilters.availability && hasMinimalRequiredFilterDefinition(legacyFilters.availability)) {
    filters[ApiFilterGroupV2.Availability] = parseApiFilter(
      ApiFilterGroupV2.Availability,
      { name: KnownFiltersV2.Availability, ...(legacyFilters.availability as Filter) },
      defaultFilters as Filters<Value>
    );
  }

  return filters;
};

export const parseApiMetaFilters = (
  { children, ...filters }: Filters,
  collection: FilteredCollectionKey,
  defaultFilters?: Filters<Value>
): FiltersMetaV2 => ({
  mainFilters: mapValues(parseApiFilters(filters, defaultFilters), (filter) => ({ ...filter, collection })),
  childrenFilters:
    children &&
    mapValues(parseApiFilters(children, defaultFilters?.children), (filter) => ({
      ...filter,
      collection,
      isChildren: true
    }))
});
